/**
 * ModelAnswerEditor — configure or display the teacher's model solution.
 *
 * In edit mode (readOnly=false): shows a CardPalette + 2 editable ShipBoards
 * so the teacher can drag-and-drop the intended correct card program.
 * In read-only mode: shows the same layout as a static reference for students
 * after they run their own battle.
 *
 * Stores programs as Card[][] — one array per ship (index matches ship order).
 * Target selectors are hidden because the activity doesn't use them.
 */
import React from 'react'
import { makeShip, type Card, type Ship, type CardType } from './engine'
import { CardPalette } from './CardPalette'
import { ShipBoard } from './ShipBoard'

interface ModelAnswerEditorProps {
  /** One Card[] per ship. Outer array length should match the player fleet size (typically 2). */
  programs: Card[][]
  /** How many ships to display. Defaults to programs.length (min 1). */
  shipCount?: number
  onChange?: (programs: Card[][]) => void
  readOnly?: boolean
  allowedCards?: string[]
  /** Show pre-parameterized palette cards and hide param dropdowns on placed cards. */
  activityMode?: boolean
}

function buildShips(programs: Card[][], count: number): Ship[] {
  const names = ['Ship 1', 'Ship 2', 'Ship 3']
  return Array.from({ length: Math.max(count, 1) }, (_, i) => ({
    ...makeShip('model', names[i] ?? `Ship ${i + 1}`),
    program: programs[i] ?? [],
  }))
}

export function ModelAnswerEditor({
  programs,
  shipCount,
  onChange,
  readOnly = false,
  allowedCards,
  activityMode = false,
}: ModelAnswerEditorProps) {
  const count = shipCount ?? Math.max(programs.length, 1)
  const [ships, setShips] = React.useState<Ship[]>(() => buildShips(programs, count))

  const updateShip = (idx: number, program: Card[]) => {
    const next = ships.map((s, i) => (i === idx ? { ...s, program } : s))
    setShips(next)
    onChange?.(next.map((s) => s.program))
  }

  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {/* Palette — hidden in read-only mode */}
      {!readOnly && (
        <div
          style={{
            width: 190,
            flexShrink: 0,
            background: 'var(--pp-surface)',
            border: '1px solid var(--pp-border)',
            borderRadius: 12,
            padding: 12,
          }}
        >
          <CardPalette allowedCards={allowedCards} activityMode={activityMode} />
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {ships.map((ship, idx) => (
          <div key={ship.id} style={{ flex: '1 1 220px' }}>
            <ShipBoard
              ship={ship}
              editable={!readOnly}
              accent="var(--pp-action-accent)"
              targets={[]}
              onProgramChange={(program) => updateShip(idx, program)}
              allowParamEdit={!activityMode}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
